import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { adminApi } from "../../api/adminApi";
import type { ParamDataType, ReportDefinition, ReportParamDefinition, ReportParamOption } from "../../api/types";
import { ReportTabs } from "../../components/ReportTabs";

function emptyParam(sortOrder: number): ReportParamDefinition {
  return {
    paramKey: "",
    label: "",
    dataType: "STRING",
    required: false,
    defaultValue: "",
    helpText: "",
    sortOrder,
    options: [],
  };
}

function emptyOption(sortOrder: number): ReportParamOption {
  return { optionValue: "", optionLabel: "", sortOrder };
}

const DATA_TYPES: ParamDataType[] = [
  "STRING",
  "NUMBER",
  "DATE",
  "DATE_RANGE",
  "BOOLEAN",
  "ENUM",
  "MULTI_ENUM",
];

export default function ParamBuilder() {
  const { reportId = "" } = useParams();
  const [report, setReport] = useState<ReportDefinition | null>(null);
  const [params, setParams] = useState<ReportParamDefinition[]>([]);
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([adminApi.reports.get(reportId), adminApi.reports.getParams(reportId)])
      .then(([r, p]) => {
        setReport(r);
        setParams(p.length > 0 ? p.sort((a, b) => a.sortOrder - b.sortOrder) : []);
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load parameters"))
      .finally(() => setLoading(false));
  }, [reportId]);

  function updateParam(index: number, patch: Partial<ReportParamDefinition>) {
    setParams((prev) => prev.map((p, i) => (i === index ? { ...p, ...patch } : p)));
  }

  function addParam() {
    setParams((prev) => [...prev, emptyParam(prev.length)]);
  }

  function removeParam(index: number) {
    setParams((prev) => prev.filter((_, i) => i !== index).map((p, i) => ({ ...p, sortOrder: i })));
  }

  function moveParam(index: number, dir: -1 | 1) {
    setParams((prev) => {
      const next = [...prev];
      const target = index + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next.map((p, i) => ({ ...p, sortOrder: i }));
    });
  }

  function addOption(paramIndex: number) {
    setParams((prev) =>
      prev.map((p, i) =>
        i === paramIndex ? { ...p, options: [...p.options, emptyOption(p.options.length)] } : p
      )
    );
  }

  function updateOption(paramIndex: number, optionIndex: number, patch: Partial<ReportParamOption>) {
    setParams((prev) =>
      prev.map((p, i) =>
        i === paramIndex
          ? { ...p, options: p.options.map((o, j) => (j === optionIndex ? { ...o, ...patch } : o)) }
          : p
      )
    );
  }

  function removeOption(paramIndex: number, optionIndex: number) {
    setParams((prev) =>
      prev.map((p, i) =>
        i === paramIndex
          ? {
              ...p,
              options: p.options
                .filter((_, j) => j !== optionIndex)
                .map((o, j) => ({ ...o, sortOrder: j })),
            }
          : p
      )
    );
  }

  function moveOption(paramIndex: number, optionIndex: number, dir: -1 | 1) {
    setParams((prev) =>
      prev.map((p, i) => {
        if (i !== paramIndex) return p;
        const options = [...p.options];
        const target = optionIndex + dir;
        if (target < 0 || target >= options.length) return p;
        [options[optionIndex], options[target]] = [options[target], options[optionIndex]];
        return { ...p, options: options.map((o, j) => ({ ...o, sortOrder: j })) };
      })
    );
  }

  async function handleSave() {
    setSaving(true);
    setSavedMessage(null);
    setError(null);
    try {
      const saved = await adminApi.reports.saveParams(reportId, params);
      setParams(saved);
      setSavedMessage("Parameters saved.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save parameters");
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <p>Loading...</p>;

  return (
    <div>
      <ReportTabs reportId={reportId} title={report?.displayName ?? "Report"} />

      {error && <div className="error-text">{error}</div>}
      {savedMessage && <div className="help-text">{savedMessage}</div>}

      <div className="page-panel">
        <p className="help-text">
          Define the inputs this report accepts. For dropdown parameters (Enum / Multi-select), add
          your own option list below — no code change needed to add or edit values.
        </p>

        {params.length === 0 && <div className="empty-state">No parameters yet.</div>}

        {params.map((param, index) => (
          <div
            key={index}
            style={{ border: "1px solid var(--color-border)", borderRadius: 6, padding: 12, marginBottom: 10 }}
          >
            <div className="form-grid">
              <div className="form-row">
                <label>Param key</label>
                <input
                  value={param.paramKey}
                  onChange={(e) => updateParam(index, { paramKey: e.target.value })}
                  placeholder="dateFrom"
                />
              </div>
              <div className="form-row">
                <label>Label</label>
                <input
                  value={param.label}
                  onChange={(e) => updateParam(index, { label: e.target.value })}
                  placeholder="Date From"
                />
              </div>
              <div className="form-row">
                <label>Data type</label>
                <select
                  value={param.dataType}
                  onChange={(e) => updateParam(index, { dataType: e.target.value as ParamDataType })}
                >
                  {DATA_TYPES.map((dt) => (
                    <option key={dt} value={dt}>
                      {dt}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-row">
                <label>Default value</label>
                <input
                  value={param.defaultValue ?? ""}
                  onChange={(e) => updateParam(index, { defaultValue: e.target.value })}
                />
              </div>
              <div className="form-row">
                <label>
                  <input
                    type="checkbox"
                    checked={param.required}
                    onChange={(e) => updateParam(index, { required: e.target.checked })}
                    style={{ marginRight: 6 }}
                  />
                  Required
                </label>
              </div>
              <div className="form-row">
                <label>Help text</label>
                <input
                  value={param.helpText ?? ""}
                  onChange={(e) => updateParam(index, { helpText: e.target.value })}
                />
              </div>
            </div>

            <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
              <button type="button" className="btn-secondary" onClick={() => moveParam(index, -1)}>
                Move up
              </button>
              <button type="button" className="btn-secondary" onClick={() => moveParam(index, 1)}>
                Move down
              </button>
              <button type="button" className="btn-danger" onClick={() => removeParam(index)}>
                Remove parameter
              </button>
              {(param.dataType === "ENUM" || param.dataType === "MULTI_ENUM") && (
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setExpanded((prev) => ({ ...prev, [index]: !prev[index] }))}
                >
                  {expanded[index] ? "Hide options" : `Options (${param.options.length})`}
                </button>
              )}
            </div>

            {(param.dataType === "ENUM" || param.dataType === "MULTI_ENUM") && expanded[index] && (
              <div style={{ background: "#f7f9fc", borderRadius: 6, padding: 10 }}>
                <div className="help-text" style={{ marginBottom: 8 }}>
                  Dropdown values shown to users of this report.
                </div>
                {param.options.map((opt, optIndex) => (
                  <div key={optIndex} style={{ display: "flex", gap: 8, marginBottom: 6, alignItems: "center" }}>
                    <input
                      placeholder="value"
                      value={opt.optionValue}
                      onChange={(e) => updateOption(index, optIndex, { optionValue: e.target.value })}
                      style={{ width: 140 }}
                    />
                    <input
                      placeholder="label"
                      value={opt.optionLabel}
                      onChange={(e) => updateOption(index, optIndex, { optionLabel: e.target.value })}
                      style={{ width: 200 }}
                    />
                    <button type="button" className="btn-secondary" onClick={() => moveOption(index, optIndex, -1)}>
                      ↑
                    </button>
                    <button type="button" className="btn-secondary" onClick={() => moveOption(index, optIndex, 1)}>
                      ↓
                    </button>
                    <button type="button" className="btn-danger" onClick={() => removeOption(index, optIndex)}>
                      Remove
                    </button>
                  </div>
                ))}
                <button type="button" className="btn-secondary" onClick={() => addOption(index)}>
                  Add option
                </button>
              </div>
            )}
          </div>
        ))}

        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
          <button type="button" className="btn-secondary" onClick={addParam}>
            Add parameter
          </button>
          <button type="button" className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save parameters"}
          </button>
        </div>
      </div>
    </div>
  );
}
